import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, mediaUrl, type Post } from '../lib/api';
import { ago, plural } from '../lib/domain';
import { md } from '../lib/md';
import { useToast } from '../hooks';
import { Avatar, ErrorBox, Icon, Loading, PageHead } from '../ui';

/** Board of messages to all members (newest first, pinned ones on top). */
export default function Mitteilungen() {
  const { data, isLoading, error } = useQuery({ queryKey: ['posts'], queryFn: () => api<Post[]>('/posts') });
  return (
    <>
      <PageHead
        title="Mitteilungen"
        right={
          <Link className="btn btn-primary" to="/mitteilungen/neu">
            <Icon name="mega" sw={1.8} />
            Mitteilung schreiben
          </Link>
        }
      />
      {isLoading ? (
        <Loading />
      ) : error ? (
        <ErrorBox error={error} />
      ) : data?.length ? (
        <div className="feed">
          {data.map(p => (
            <PostCard key={p.id} p={p} />
          ))}
        </div>
      ) : (
        <div className="empty">
          <b>Noch keine Mitteilungen.</b>
          <p>
            Mitteilungen ersetzen die Rundmail: alle bekommen sie per E-Mail oder Push, und sie bleiben hier zum
            Nachlesen.
          </p>
          <Link className="btn btn-primary" to="/mitteilungen/neu">
            <Icon name="mega" sw={1.8} />
            Erste Mitteilung schreiben
          </Link>
        </div>
      )}
    </>
  );
}

function PostCard({ p }: { p: Post }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [confirm, setConfirm] = useState(false);
  const remove = useMutation({
    mutationFn: () => api(`/posts/${p.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast('Mitteilung gelöscht');
      void qc.invalidateQueries({ queryKey: ['posts'] });
    },
    onError: e => toast(e.message),
  });
  return (
    <article className={`post${p.pinned ? ' pinned' : ''}`} id={`p${p.id}`}>
      <div className="post-head">
        {p.author ? <Avatar person={p.author} size="md" /> : <span className="av md">–</span>}
        <span className="row-txt">
          <b>
            {p.author?.id ? (
              <Link to={`/mitglieder/${p.author.id}`}>{p.author.name}</Link>
            ) : (
              (p.author?.name ?? 'Unbekannt')
            )}
          </b>
          <small>
            {ago(new Date(p.created_at))}
            {p.edited_at ? ' · bearbeitet' : ''}
          </small>
        </span>
        {p.pinned && <span className="st st-new">Wichtig</span>}
      </div>
      <h3>{p.title}</h3>
      <div className="body desc" dangerouslySetInnerHTML={{ __html: md(p.body) }} />
      {p.images.length > 0 && (
        <div className={`post-pics n${Math.min(p.images.length, 3)}`}>
          {p.images.map(i => (
            <a
              key={i.id}
              href={mediaUrl(i.file)!}
              target="_blank"
              rel="noopener"
              aria-label="Bild in voller Größe öffnen"
            >
              <img src={mediaUrl(i.thumb)!} alt="" loading="lazy" />
            </a>
          ))}
        </div>
      )}
      <div className="delivery">
        <span>
          <Icon name="mail" sw={1.8} />
          {p.recipients ? `E-Mail an ${plural(p.recipients, 'Mitglied', 'Mitglieder')}` : 'ohne E-Mail'}
        </span>
        {p.can_edit && (
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 12 }}>
            <Link className="linkbtn" to={`/mitteilungen/${p.id}/bearbeiten`}>
              Bearbeiten
            </Link>
            <button className="linkbtn" onClick={() => setConfirm(true)}>
              Löschen
            </button>
          </span>
        )}
      </div>
      {confirm && (
        <div className="confirm" role="alert">
          <p>Mitteilung löschen? Bereits verschickte E-Mails bleiben bei den Mitgliedern.</p>
          <button className="btn btn-ghost btn-sm" onClick={() => setConfirm(false)}>
            Abbrechen
          </button>
          <button className="btn btn-danger btn-sm" onClick={() => remove.mutate()}>
            Löschen
          </button>
        </div>
      )}
    </article>
  );
}
